import { Head, router } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { useConfirmDialog } from "@/components/confirm-dialog-provider";
import AuthLayout from "@/layouts/auth-layout";
import Drawer from "@/components/drawer";
import StepEditDrawer from "@/pages/steps/step-edit-drawer";
import { product_path, product_step_path, products_path } from "@/routes";

function ShowStep({ product, step, templates = [] }) {
  return (
    <>
      <Head title={step.name} />

      <section className="workspace-content">
        <div className="card workspace-product-summary">
          <div className="card-body">
            {step.tag ? <div className="workspace-card-eyebrow">{step.tag}</div> : null}

            <p className="workspace-product-summary__description">{step.description}</p>

            {step.step_participants.length > 0 ? (
              <div className="workspace-step-summary__roles">
                <div className="workspace-role-list workspace-role-list--summary">
                  {step.step_participants.map((participant) => (
                    <div key={participant.id} className="workspace-role-list__item">
                      <span className="workspace-role-list__name">{participant.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <section className="workspace-section">
          <div className="row g-4">
            <div className="col-xl-7">
              <div className="card h-100">
                <div className="card-body">
                  <h2 className="workspace-section__title">Sådan gennemføres trinnet</h2>

                  {step.sub_steps.length === 0 ? (
                    <div className="workspace-empty mt-4">
                      <h3 className="workspace-empty__title">Ingen undertrin endnu</h3>
                      <p className="workspace-empty__copy">Tilføj undertrin for at beskrive arbejdsforløbet mere konkret.</p>
                    </div>
                  ) : (
                    <div className="workspace-step-sequence mt-4">
                      {step.sub_steps.map((subStep) => (
                        <details key={subStep.id} className="workspace-step-sequence__item">
                          <summary className="workspace-step-sequence__summary">
                            <span className="workspace-step-sequence__index">{subStep.index}</span>
                            <span className="workspace-step-sequence__name">{subStep.name}</span>
                            <span className="workspace-step-sequence__toggle" aria-hidden="true">
                              <i className="bi bi-chevron-down" />
                            </span>
                          </summary>
                          <p className="workspace-step-sequence__description">
                            {subStep.description?.trim() || "Ingen beskrivelse endnu."}
                          </p>
                        </details>
                      ))}
                    </div>
                  )}

                  <div className="workspace-step-help-note">
                    <div className="workspace-step-help-note__icon" aria-hidden="true">
                      <i className="bi bi-life-preserver" />
                    </div>
                    <div className="workspace-step-help-note__content">
                      <p className="workspace-step-help-note__copy">
                        <strong>Brug for hjælp?</strong> Tag kontakt, hvis du vil have sparring til at gennemføre trinnet eller afklare næste skridt.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-5 workspace-step-doc-column">
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <h2 className="workspace-section__title">Dokumenter</h2>
                    </div>
                    <span className="workspace-chip">
                      {step.templates.length} {step.templates.length === 1 ? "dokument" : "dokumenter"}
                    </span>
                  </div>

                  {step.templates.length === 0 ? (
                    <p className="workspace-empty__copy mt-4 mb-0">Ingen dokumenter er knyttet til trinnet endnu.</p>
                  ) : (
                    <div className="workspace-step-doc-list mt-3">
                      {step.templates.map((templateItem) => (
                        <div key={templateItem.id} className="workspace-step-doc-row">
                          <div className="workspace-step-doc-row__content">
                            <div className="workspace-step-doc-row__title" title={templateItem.name}>
                              {templateItem.name}
                            </div>
                          </div>
                          <div className="workspace-step-doc-row__actions">
                            {templateItem.file ? (
                              <a
                                href={templateItem.download_url}
                                className="btn btn-sm btn-primary workspace-step-doc-row__action"
                              >
                                Hent fil
                              </a>
                            ) : !templateItem.filled ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary workspace-step-doc-row__action"
                                  onClick={() => router.post(templateItem.create_url)}
                                  disabled={!templateItem.create_url}
                                >
                                  Opret dokument
                                </button>
                                {templateItem.docx_url ? (
                                  <a
                                    href={templateItem.docx_url}
                                    className="btn btn-sm btn-outline-secondary workspace-step-doc-row__action"
                                  >
                                    DOCX
                                  </a>
                                ) : null}
                              </>
                            ) : (
                              templateItem.edit_url ? (
                                <a
                                  href={templateItem.edit_url}
                                  className="btn btn-sm btn-primary workspace-step-doc-row__action"
                                >
                                  Redigér
                                </a>
                              ) : null
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="workspace-step-summary__criteria workspace-step-summary__criteria--standalone">
                <div className="workspace-step-summary__criteria-header">
                  <span className="workspace-step-summary__criteria-icon" aria-hidden="true">
                    <i className="bi bi-check2" />
                  </span>
                  <h3 className="workspace-step-summary__criteria-title">Løsningskriterier</h3>
                </div>
                <p className="workspace-step-summary__criteria-copy">
                  {step.completion_criteria || "Der er endnu ikke beskrevet kriterier for, hvornår dette trin kan betragtes som løst."}
                </p>
              </div>
            </div>
          </div>
        </section>
      </section>
    </>
  );
}

function StepShowLayout({ page }) {
  const {
    product,
    step,
    editable_step: editableStep,
    edit_step_errors: editStepErrors = [],
    can_edit_step: canEditStep = false,
    show_edit_step_drawer: showEditStepDrawer = false
  } = page.props;
  const { confirm } = useConfirmDialog();
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(canEditStep && showEditStepDrawer);
  const [isEditDrawerSubmitting, setIsEditDrawerSubmitting] = useState(false);

  useEffect(() => {
    if (canEditStep && showEditStepDrawer) {
      setIsEditDrawerOpen(true);
    }
  }, [canEditStep, showEditStepDrawer]);

  const destroyStep = async () => {
    const confirmed = await confirm({
      title: "Slet trin",
      description: `Vil du slette "${step.name}"?`,
      confirmLabel: "Slet",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(product_step_path(product.id, step.id));
  };

  return (
    <AuthLayout
      header={{
        breadcrumbs: [
          { label: "Produkter", href: products_path() },
          { label: product.name, href: product_path(product.id) },
          { label: step.name }
        ],
        title: step.name,
        actions: [
          {
            href: product_path(product.id),
            label: "Tilbage",
            variant: "outline-secondary"
          }
        ].concat(canEditStep ? [
          {
            onClick: () => setIsEditDrawerOpen(true),
            label: "Redigér",
            variant: "primary"
          },
          {
            label: "Slet",
            variant: "danger",
            onClick: destroyStep
          }
        ] : [])
      }}
    >
      {page}
      {canEditStep ? (
        <Drawer
          isOpen={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          title={`Redigér ${step.name}`}
          size="lg"
          closeDisabled={isEditDrawerSubmitting}
        >
          <StepEditDrawer
            step={editableStep}
            templates={templates}
            errors={editStepErrors}
            updatePath={`${product_step_path(product.id, step.id)}?return_to=show`}
            onSuccess={() => setIsEditDrawerOpen(false)}
            onProcessingChange={setIsEditDrawerSubmitting}
          />
        </Drawer>
      ) : null}
    </AuthLayout>
  );
}

ShowStep.layout = (page) => <StepShowLayout page={page} />;

export default ShowStep;
